import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Server, Plus, Radar, Trash2 } from "lucide-react";
import { apiGet, protectedPost, protectedDelete } from "@/lib/api";
import { cn } from "@/lib/utils";
import { ModulePage, Label, INPUT, BTN_PRIMARY, BTN_GHOST, BTN_DANGER, Empty, NotConnected, fmtTime } from "@/components/common/ModuleShell";

const blank = { name: "", host: "", role: "general", provider: "", region: "", ports: "22", notes: "" };

function ServerForm({ onSaved, onCancel }) {
  const [form, setForm] = useState(blank), [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const ports = form.ports.split(",").map((p) => Number(p.trim())).filter((p) => p > 0 && p < 65536);
      await protectedPost("/servers", { ...form, ports: ports.length ? ports : [22] });
      toast.success("Server registered."); onSaved();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="jv-panel p-5 grid gap-3 md:grid-cols-3" data-testid="server-form">
      <div><Label>Name</Label><input data-testid="server-name" className={INPUT} value={form.name} onChange={set("name")} required /></div>
      <div><Label>Host / IP</Label><input data-testid="server-host" className={INPUT} value={form.host} onChange={set("host")} required placeholder="vps.example.com" /></div>
      <div><Label>Role</Label><input data-testid="server-role" className={INPUT} value={form.role} onChange={set("role")} placeholder="omniroute, worker, db…" /></div>
      <div><Label>Provider</Label><input data-testid="server-provider" className={INPUT} value={form.provider} onChange={set("provider")} placeholder="OVH" /></div>
      <div><Label>Region</Label><input data-testid="server-region" className={INPUT} value={form.region} onChange={set("region")} placeholder="BHS / Canada" /></div>
      <div><Label>Probe ports (comma-separated)</Label><input data-testid="server-ports" className={INPUT} value={form.ports} onChange={set("ports")} /></div>
      <div className="md:col-span-3"><Label>Notes</Label><input data-testid="server-notes" className={INPUT} value={form.notes} onChange={set("notes")} maxLength={300} /></div>
      <div className="md:col-span-3 flex gap-2">
        <button type="submit" disabled={busy} className={BTN_PRIMARY} data-testid="server-save">Register</button>
        <button type="button" onClick={onCancel} className={BTN_GHOST} data-testid="server-cancel">Cancel</button>
      </div>
    </form>
  );
}

function PortChip({ p }) {
  return <span className={cn("font-num text-[11px] px-2 py-0.5 rounded border", p.open ? "border-jarvis-cyan/40 text-jarvis-cyanBright" : "border-jarvis-red/40 text-jarvis-red")} data-testid={`port-${p.port}`}>
    :{p.port} {p.open ? `${p.latency_ms} ms` : p.error}
  </span>;
}

function ServerCard({ s, onProbe, onDelete, probing }) {
  const probe = s.last_probe;
  return (
    <div className="jv-panel p-4" data-testid={`server-card-${s.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={cn("w-2 h-2 rounded-full", probe ? (probe.reachable ? "bg-jarvis-cyan jv-pulse" : "bg-jarvis-red") : "bg-slate-600")} />
            <span className="font-ui text-base text-slate-100">{s.name}</span>
            <span className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-white/10 text-slate-400">{s.role}</span>
          </div>
          <div className="font-code text-[11px] text-jarvis-cyan/70 mt-0.5">{s.host}{s.provider ? ` · ${s.provider}` : ""}{s.region ? ` · ${s.region}` : ""}</div>
          {s.notes && <div className="text-xs text-slate-400 mt-1">{s.notes}</div>}
        </div>
        <div className="flex gap-2 shrink-0">
          <button onClick={() => onProbe(s)} disabled={probing} className={BTN_GHOST} data-testid={`server-probe-${s.id}`}><Radar className={cn("w-3.5 h-3.5", probing && "animate-spin")} />Probe</button>
          <button onClick={() => onDelete(s)} className={BTN_DANGER} data-testid={`server-delete-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {probe ? <>{probe.ports.map((p) => <PortChip key={p.port} p={p} />)}<span className="font-mono text-[10px] text-slate-500">probed {fmtTime(probe.at)}</span></>
          : <span className="font-mono text-[10px] text-slate-500" data-testid={`server-unprobed-${s.id}`}>Not probed yet — TCP reachability measured from the JARVIS host</span>}
        <NotConnected testId={`server-telemetry-${s.id}`}>CPU / RAM / disk telemetry not connected</NotConnected>
      </div>
    </div>
  );
}

export default function ServersPage() {
  const [servers, setServers] = useState(null), [adding, setAdding] = useState(false), [probing, setProbing] = useState(null);
  const load = useCallback(() => apiGet("/servers").then(setServers).catch((e) => toast.error(e.message)), []);
  useEffect(() => { load(); }, [load]);
  const probe = async (s) => {
    setProbing(s.id);
    try { const r = await protectedPost(`/servers/${s.id}/probe`, {}); toast[r.reachable ? "success" : "error"](`${s.name}: ${r.reachable ? "reachable" : "unreachable"}`); load(); }
    catch (e) { toast.error(e.message); } finally { setProbing(null); }
  };
  const remove = async (s) => {
    if (!window.confirm(`Remove "${s.name}" from the registry?`)) return;
    try { await protectedDelete(`/servers/${s.id}`); load(); } catch (e) { toast.error(e.message); }
  };
  const reachable = servers?.filter((s) => s.last_probe?.reachable).length || 0;
  return (
    <ModulePage icon={Server} title="Servers" testId="servers-page"
      subtitle={servers ? `${servers.length} registered · ${reachable} reachable on last probe · telemetry collector not connected` : "Loading…"}
      right={!adding && <button onClick={() => setAdding(true)} className={BTN_PRIMARY} data-testid="server-add"><Plus className="w-3.5 h-3.5" />Register server</button>}>
      {adding && <div className="mb-5"><ServerForm onSaved={() => { setAdding(false); load(); }} onCancel={() => setAdding(false)} /></div>}
      <div className="grid gap-3 xl:grid-cols-2">
        {servers?.length === 0 && <Empty testId="servers-empty">No servers registered. Add your OmniRoute VPS to start probing reachability.</Empty>}
        {servers?.map((s) => <ServerCard key={s.id} s={s} onProbe={probe} onDelete={remove} probing={probing === s.id} />)}
      </div>
    </ModulePage>
  );
}
